import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Divider,
  Form,
  FormGroup,
  FormSelect,
  FormSelectOption,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  Spinner,
  TextArea,
  TextInput
} from "@patternfly/react-core";
import { Table, Tbody, Td, Th, Thead, Tr } from "@patternfly/react-table";

import { apiGet, apiPost } from "../app/api";
import {
  compactDateTime,
  formatQuantity,
  plainNextAction,
  requestDateText,
  requestDocumentNumber,
  requestLocationText
} from "../display";
import type {
  DashboardOrder,
  Driver,
  DriversPayload,
  OrderDetailLine,
  OrderFulfillmentLine,
  PurchaseOrderDetailPayload,
  User,
  WorkflowResult
} from "../types";

type Message = { variant: "success" | "danger"; title: string };
type QuantityByLine = Record<number, string>;

type OrderDetailDialogProps = {
  orderId: string | number | null;
  user: User;
  csrfToken: string;
  onClose: () => void;
  onChanged: () => void;
};

type RequestLocationBackfill = {
  request_location_id?: number;
};

function documentKind(order: DashboardOrder | null): string {
  if (!order) return "Order";
  if (order.entity_type === "invoice") return "Invoice";
  if (order.entity_type === "delivery_receipt") return "Delivery Receipt";
  if (order.entity_type === "in_house_request") return "In-House Request";
  return "Branch Request";
}

function lineRequestedQuantity(line: OrderDetailLine): number {
  return Number(line.branch_requested_qty ?? line.original_quantity ?? line.quantity ?? 0);
}

function lineAcceptedQuantity(line: OrderDetailLine): number {
  return Number(line.accepted_quantity ?? line.quantity ?? lineRequestedQuantity(line));
}

function lineId(line: OrderDetailLine): number {
  return Number(line.line_id ?? line.id);
}

function noticeText(order: DashboardOrder | null): string {
  if (!order) return "";
  return String(order.adjustment_notice || order.contest_notes || order.resolved_reason || order.contest_reason || "");
}

function isWarehouseGoodsUser(user: User): boolean {
  return ["Warehouse Wet Goods", "Warehouse Dry Goods"].includes(user.role);
}

function canManageWarehouse(user: User): boolean {
  return ["System Admin", "Super Admin", "Purchaser", "Warehouse SL"].includes(user.role) || isWarehouseGoodsUser(user);
}

function canResolveDeliveryIssue(user: User): boolean {
  return ["System Admin", "Super Admin", "Warehouse SL"].includes(user.role);
}

function canBranchRespond(order: DashboardOrder | null, user: User): boolean {
  if (!order) return false;
  if (["System Admin", "Super Admin"].includes(user.role)) return true;
  const requestLocationId = Number((order as DashboardOrder & RequestLocationBackfill).request_location_id || 0);
  return user.role === "Location Lead" && (!requestLocationId || !user.location_id || Number(user.location_id) === requestLocationId);
}

function actionSet(order: DashboardOrder | null, user: User): string[] {
  if (!order) return [];
  const status = String(order.status || "");
  if (status.startsWith("Closed") || status === "Resolved") return [];
  const actions: string[] = [];
  if (order.entity_type === "invoice" || order.entity_type === "delivery_receipt") {
    if (["Dispatched - Awaiting Branch Response", "DR - Awaiting Branch Confirmation"].includes(status) && canBranchRespond(order, user)) {
      actions.push("invoice_approve", "invoice_contest");
    }
    if (["Contested", "DR Contested"].includes(status) && canResolveDeliveryIssue(user)) actions.push("invoice_resolve");
    return actions;
  }
  if (["Submitted", "Awaiting Warehouse Acceptance", "Submitted to COMW"].includes(status) && canManageWarehouse(user)) {
    actions.push("accept_first");
  }
  if (["Accepted - Waiting Fill", "Partially Filled"].includes(status) && Number(order.unserved_total || 0) > 0 && canManageWarehouse(user)) {
    actions.push("fill");
  }
  if (Number((order as DashboardOrder & { ready_to_ship_qty?: number }).ready_to_ship_qty || 0) > 0 && canResolveDeliveryIssue(user)) {
    actions.push("ready_to_ship");
  }
  if (["Awaiting delivery", "Branch Acknowledgement Required", "Edited - Branch Approval Required"].includes(status) && canBranchRespond(order, user)) {
    actions.push("branch_approve");
    if (status !== "Edited - Branch Approval Required") actions.push("branch_contest");
  }
  if (status === "Contested" && canResolveDeliveryIssue(user)) actions.push("resolve_contested");
  return actions;
}

function actionLabel(action: string): string {
  const labels: Record<string, string> = {
    accept_first: "Accept request",
    fill: "Fill this request",
    ready_to_ship: "Create Delivery Receipt",
    branch_approve: "Accept",
    branch_contest: "Report problem",
    invoice_approve: "Accept Delivery Receipt",
    invoice_contest: "Report problem",
    invoice_resolve: "Resolve issue",
    resolve_contested: "Resolve issue"
  };
  return labels[action] || action.replaceAll("_", " ");
}

function orderLineLabel(line: OrderDetailLine | OrderFulfillmentLine): string {
  return [line.sku, line.product_name].filter(Boolean).join(" - ") || "Item";
}

export function OrderDetailDialog({ orderId, user, csrfToken, onClose, onChanged }: OrderDetailDialogProps) {
  const [order, setOrder] = useState<DashboardOrder | null>(null);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);
  const [selectedAction, setSelectedAction] = useState("");
  const [reviewQuantities, setReviewQuantities] = useState<QuantityByLine>({});
  const [fillQuantities, setFillQuantities] = useState<QuantityByLine>({});
  const [driverId, setDriverId] = useState("");
  const [deliverySlot, setDeliverySlot] = useState("");
  const [reason, setReason] = useState("missing_item");
  const [notes, setNotes] = useState("");

  const isOpen = orderId !== null;

  const resetActionState = useCallback((nextOrder: DashboardOrder | null) => {
    const reviewDefaults: QuantityByLine = {};
    for (const line of nextOrder?.lines || []) {
      reviewDefaults[lineId(line)] = String(Math.round(lineAcceptedQuantity(line)));
    }
    const fillDefaults: QuantityByLine = {};
    for (const line of nextOrder?.fulfillment || []) {
      if (Number(line.unserved_qty || 0) > 0) fillDefaults[line.line_id] = String(Math.round(Number(line.unserved_qty || 0)));
    }
    setReviewQuantities(reviewDefaults);
    setFillQuantities(fillDefaults);
    setDriverId(nextOrder?.driver_id ? String(nextOrder.driver_id) : "");
    setDeliverySlot(nextOrder?.delivery_slot_at ? String(nextOrder.delivery_slot_at).slice(0, 16) : "");
    setReason("missing_item");
    setNotes("");
    setSelectedAction("");
  }, []);

  const loadOrder = useCallback(async (id: string | number) => {
    setLoading(true);
    setMessage(null);
    try {
      const payload = await apiGet<PurchaseOrderDetailPayload>(`/api/purchase-orders/detail?id=${encodeURIComponent(String(id))}`);
      setOrder(payload.purchase_order);
      resetActionState(payload.purchase_order);
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Order details could not be loaded." });
      setOrder(null);
      resetActionState(null);
    } finally {
      setLoading(false);
    }
  }, [resetActionState]);

  useEffect(() => {
    if (orderId !== null) void loadOrder(orderId);
  }, [loadOrder, orderId]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    void apiGet<DriversPayload>("/api/drivers")
      .then(payload => {
        if (!cancelled) setDrivers((payload.drivers || []).filter(driver => Number(driver.active ?? 1) === 1));
      })
      .catch(() => {
        if (!cancelled) setDrivers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const actions = useMemo(() => actionSet(order, user), [order, user]);
  const selectedOrDefaultAction = selectedAction || actions[0] || "";
  const fulfillmentRows = useMemo(() => (order?.fulfillment || []).filter(line => Number(line.unserved_qty || 0) > 0), [order?.fulfillment]);

  const submitWorkflow = useCallback(async (action: string) => {
    if (!order) return;
    setSaving(true);
    setMessage(null);
    try {
      let path = "/api/purchase-orders/workflow";
      let body: Record<string, unknown> = { po_id: order.id, action };
      if (action === "accept_first") {
        body = {
          ...body,
          adjustments: (order.lines || []).map(line => ({ line_id: lineId(line), quantity: Number(reviewQuantities[lineId(line)] || 0) })),
          driver_id: Number(driverId || 0),
          delivery_slot_at: deliverySlot
        };
      } else if (action === "fill") {
        path = "/api/warehouse-job-orders";
        body = {
          po_id: order.id,
          lines: fulfillmentRows.map(line => ({ line_id: line.line_id, quantity: Number(fillQuantities[line.line_id] || 0) })),
          notes
        };
      } else if (action === "branch_approve" || action === "branch_contest") {
        path = "/api/purchase-orders/branch-response";
        body = {
          po_id: order.id,
          decision: action === "branch_approve" ? "approve" : "contest",
          reason,
          notes
        };
      } else if (action === "invoice_approve" || action === "invoice_contest") {
        path = "/api/dispatch-invoices/branch-response";
        body = {
          invoice_id: order.entity_id,
          decision: action === "invoice_approve" ? "approve" : "contest",
          reason: reason === "missing_item" ? "quantity_difference" : reason,
          notes,
          adjustments: (order.lines || []).map(line => ({ line_id: lineId(line), quantity: Number(reviewQuantities[lineId(line)] || 0) }))
        };
      } else if (action === "invoice_resolve") {
        path = "/api/dispatch-invoices/resolve";
        body = { invoice_id: order.entity_id, resolution_note: notes };
      } else if (action === "resolve_contested") {
        body = { ...body, reason: notes };
      } else if (action === "ready_to_ship") {
        body = { ...body, adjustments: [] };
      }
      const result = await apiPost<WorkflowResult>(path, body, csrfToken);
      setMessage({
        variant: "success",
        title: result.delivery_receipt_number
          ? `Saved. Delivery Receipt ${result.delivery_receipt_number} was created.`
          : "Saved. Order status was updated."
      });
      onChanged();
      if (result.invoice_id) {
        await loadOrder(`invoice-${result.invoice_id}`);
      } else {
        await loadOrder(order.id);
      }
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "The order could not be updated." });
    } finally {
      setSaving(false);
    }
  }, [csrfToken, deliverySlot, driverId, fillQuantities, fulfillmentRows, loadOrder, notes, onChanged, order, reason, reviewQuantities]);

  const requiresReason = ["branch_contest", "invoice_contest"].includes(selectedOrDefaultAction);
  const requiresNotes = ["invoice_resolve", "resolve_contested"].includes(selectedOrDefaultAction) || (requiresReason && reason === "other");
  const needsWarehouseAssignment = selectedOrDefaultAction === "accept_first" && user.role === "Warehouse SL";
  const canSubmit = Boolean(selectedOrDefaultAction)
    && !saving
    && (!requiresNotes || notes.trim().length > 0)
    && (!needsWarehouseAssignment || (driverId !== "" && deliverySlot !== ""));

  return (
    <Modal
      variant={ModalVariant.large}
      isOpen={isOpen}
      onClose={onClose}
      className="pf-toj-order-detail-dialog"
    >
      <ModalHeader title={order ? `${documentKind(order)} ${requestDocumentNumber(order)}` : "Order details"} />
      <ModalBody>
        {loading && <Spinner size="lg" aria-label="Loading order details" />}
        {message && <Alert variant={message.variant} title={message.title} isInline className="pf-toj-inline-alert" />}
        {order && (
          <div id="pf-order-detail-dialog" className="pf-toj-order-detail">
            <div className="pf-toj-order-summary">
              <div>
                <span>Requester</span>
                <strong>{requestLocationText(order) || "Not shown"}</strong>
              </div>
              <div>
                <span>Status</span>
                <strong>{order.status || "Open"}</strong>
              </div>
              <div>
                <span>Current Status</span>
                <strong>{plainNextAction(order)}</strong>
              </div>
              <div>
                <span>Needed By</span>
                <strong>{requestDateText(order.requested_delivery || order.created_at)}</strong>
              </div>
              {order.driver_name && (
                <div>
                  <span>Driver</span>
                  <strong>{order.driver_name}</strong>
                </div>
              )}
              {order.delivery_slot_at && (
                <div>
                  <span>Delivery Slot</span>
                  <strong>{compactDateTime(order.delivery_slot_at)}</strong>
                </div>
              )}
            </div>
            {noticeText(order) && <Alert variant="warning" title={noticeText(order)} isInline />}
            <Divider />
            <div className="pf-toj-table-wrap">
              <Table aria-label="Order detail lines" variant="compact">
                <Thead>
                  <Tr>
                    <Th>Item</Th>
                    <Th>Unit</Th>
                    <Th textCenter>Requested</Th>
                    <Th textCenter>Accepted</Th>
                    <Th textCenter>Unserved</Th>
                    {selectedOrDefaultAction === "accept_first" && <Th>Accepted Qty</Th>}
                  </Tr>
                </Thead>
                <Tbody>
                  {(order.lines || []).map(line => {
                    const accepted = lineAcceptedQuantity(line);
                    const requested = lineRequestedQuantity(line);
                    const fulfillment = (order.fulfillment || []).find(item => Number(item.line_id) === lineId(line));
                    return (
                      <Tr key={lineId(line)}>
                        <Td dataLabel="Item"><strong>{orderLineLabel(line)}</strong></Td>
                        <Td dataLabel="Unit">{line.uom || ""}</Td>
                        <Td dataLabel="Requested" textCenter>{formatQuantity(requested)}</Td>
                        <Td dataLabel="Accepted" textCenter>{formatQuantity(accepted)}</Td>
                        <Td dataLabel="Unserved" textCenter>{formatQuantity(fulfillment?.unserved_qty || 0)}</Td>
                        {selectedOrDefaultAction === "accept_first" && (
                          <Td dataLabel="Accepted Qty">
                            <TextInput
                              aria-label={`Accepted quantity ${lineId(line)}`}
                              type="number"
                              min="0"
                              max={requested}
                              value={reviewQuantities[lineId(line)] || ""}
                              onChange={(_event, value) => setReviewQuantities(current => ({ ...current, [lineId(line)]: String(value) }))}
                            />
                          </Td>
                        )}
                      </Tr>
                    );
                  })}
                  {!(order.lines || []).length && (
                    <Tr><Td colSpan={selectedOrDefaultAction === "accept_first" ? 6 : 5}>No line items are attached to this document.</Td></Tr>
                  )}
                </Tbody>
              </Table>
            </div>
            {fulfillmentRows.length > 0 && selectedOrDefaultAction === "fill" && (
              <div className="pf-toj-table-wrap">
                <Table aria-label="Fill order lines" variant="compact">
                  <Thead>
                    <Tr>
                      <Th>Item</Th>
                      <Th textCenter>Available</Th>
                      <Th textCenter>Unserved</Th>
                      <Th>Fill Qty</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {fulfillmentRows.map(line => (
                      <Tr key={line.line_id}>
                        <Td dataLabel="Item">{orderLineLabel(line)}</Td>
                        <Td dataLabel="Available" textCenter>{formatQuantity(line.warehouse_available || 0)}</Td>
                        <Td dataLabel="Unserved" textCenter>{formatQuantity(line.unserved_qty || 0)}</Td>
                        <Td dataLabel="Fill Qty">
                          <TextInput
                            aria-label={`Fill quantity ${line.line_id}`}
                            type="number"
                            min="0"
                            max={Number(line.unserved_qty || 0)}
                            value={fillQuantities[line.line_id] || ""}
                            onChange={(_event, value) => setFillQuantities(current => ({ ...current, [line.line_id]: String(value) }))}
                          />
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </div>
            )}
            {actions.length > 0 && (
              <Form className="pf-toj-order-action-form">
                <FormGroup label="Available action" fieldId="pf-order-action">
                  <FormSelect id="pf-order-action" value={selectedOrDefaultAction} onChange={(_event, value) => setSelectedAction(String(value))}>
                    {actions.map(action => <FormSelectOption key={action} value={action} label={actionLabel(action)} />)}
                  </FormSelect>
                </FormGroup>
                {needsWarehouseAssignment && (
                  <>
                    <FormGroup label="Driver" isRequired fieldId="pf-order-driver">
                      <FormSelect id="pf-order-driver" value={driverId} onChange={(_event, value) => setDriverId(String(value))}>
                        <FormSelectOption value="" label="Choose driver" />
                        {drivers.map(driver => <FormSelectOption key={driver.id} value={String(driver.id)} label={driver.phone ? `${driver.driver_name} - ${driver.phone}` : driver.driver_name} />)}
                      </FormSelect>
                    </FormGroup>
                    <FormGroup label="Delivery date and time" isRequired fieldId="pf-order-delivery-slot">
                      <TextInput id="pf-order-delivery-slot" type="datetime-local" value={deliverySlot} onChange={(_event, value) => setDeliverySlot(String(value))} />
                    </FormGroup>
                  </>
                )}
                {requiresReason && (
                  <FormGroup label="Problem type" fieldId="pf-order-reason">
                    <FormSelect id="pf-order-reason" value={reason} onChange={(_event, value) => setReason(String(value))}>
                      <FormSelectOption value="missing_item" label="Missing item" />
                      <FormSelectOption value="damaged_item" label="Damaged item" />
                      <FormSelectOption value="wrong_item" label="Wrong item" />
                      <FormSelectOption value="other" label="Other" />
                    </FormSelect>
                  </FormGroup>
                )}
                {(requiresReason || ["fill", "invoice_resolve", "resolve_contested"].includes(selectedOrDefaultAction)) && (
                  <FormGroup label="Notes" isRequired={requiresNotes} fieldId="pf-order-notes">
                    <TextArea id="pf-order-notes" value={notes} onChange={(_event, value) => setNotes(String(value))} />
                  </FormGroup>
                )}
              </Form>
            )}
            {!actions.length && <Label color="grey">No action is available for this role or this status.</Label>}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        {actions.length > 0 && (
          <Button variant="primary" isLoading={saving} isDisabled={!canSubmit} onClick={() => void submitWorkflow(selectedOrDefaultAction)}>
            {actionLabel(selectedOrDefaultAction)}
          </Button>
        )}
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </ModalFooter>
    </Modal>
  );
}
